import api from "./api";

export interface Contrato {
  id: string;
  projetoId: string;
  modalidade: "unico" | "parcelado" | "mensalidade" | "setup_mensalidade";
  valorTotal: number;
  valorSetup?: number | null;
  status: string;
  observacoes: string | null;
  contratoAssinaturaId?: string | null;
  contratoAssinadoEm?: string | null;
  projeto: { id: string; nome: string; cliente: string };
  parcelas: Parcela[];
  mensalidade?: {
    valorMensal: number;
    diaVencimento: number | null;
    inicioCobranca: string | null;
    fimCobranca?: string | null;
    ativa?: boolean;
    canceladaEm?: string | null;
    motivoCancelamento?: string | null;
  } | null;
  valorPago?: number;
  saldoRestante?: number;
}

export interface Parcela {
  id: string;
  numero: number;
  valor: number;
  vencimento: string;
  status: string;
  dataPagamento: string | null;
  valorPago: number | null;
  formaPagamento: string | null;
}

export async function listarContratos(filtros?: { status?: string; modalidade?: string }) {
  const params = new URLSearchParams();
  if (filtros?.status) params.append("status", filtros.status);
  if (filtros?.modalidade) params.append("modalidade", filtros.modalidade);
  const response = await api.get(`/financeiro/contratos?${params.toString()}`);
  return response.data.data;
}

export async function buscarContrato(id: string) {
  const response = await api.get(`/financeiro/contratos/${id}`);
  return response.data.data as Contrato;
}

export async function criarContrato(data: {
  projetoId: string;
  modalidade: string;
  valorTotal: number;
  valorSetup?: number;
  observacoes?: string;
  numParcelas?: number;
  primeiraParcelaVencimento?: string;
  valorEntrada?: number;
  valorMensal?: number;
  diaVencimento?: number;
  inicioCobranca?: string;
  fimCobranca?: string;
  carenciaDias?: number;
  contratoAssinadoEm?: string;
  valorSetupPago?: number;
  valorRestanteVencimento?: string;
}) {
  const response = await api.post("/financeiro/contratos", data);
  return response.data.data;
}

export async function recalcularParcelasPendentes(contratoId: string) {
  const r = await api.post(`/financeiro/contratos/${contratoId}/recalcular-parcelas`);
  return r.data.data as { recriadas: number };
}

export async function buscarContratoPorProjeto(projetoId: string) {
  try {
    const r = await api.get(`/financeiro/contratos/projeto/${projetoId}`);
    return r.data.data as Contrato | null;
  } catch (e: any) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

export async function registrarPagamento(parcelaId: string, data: {
  dataPagamento: string;
  valorPago: number;
  formaPagamento: string;
}) {
  const response = await api.post(`/financeiro/parcelas/${parcelaId}/pagamento`, data);
  return response.data.data;
}

export async function listarParcelasVencidas() {
  const response = await api.get("/financeiro/parcelas/vencidas");
  return response.data.data as Parcela[];
}

export async function atualizarParcela(parcelaId: string, dados: { valor?: number; vencimento?: string; status?: string }) {
  const r = await api.patch(`/financeiro/parcelas/${parcelaId}`, dados);
  return r.data.data as Parcela;
}

export async function deletarParcela(parcelaId: string) {
  await api.delete(`/financeiro/parcelas/${parcelaId}`);
}

export async function estornarPagamento(parcelaId: string) {
  const r = await api.post(`/financeiro/parcelas/${parcelaId}/estornar`);
  return r.data.data as Parcela;
}

export interface ListagemParcelas {
  parcelas: Array<Parcela & { contrato: { id: string; projeto: { id: string; nome: string; cliente: string } } }>;
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export async function listarParcelas(filtros: { status?: string; mes?: string; busca?: string; projetoId?: string; page?: number; perPage?: number }) {
  const params = new URLSearchParams();
  if (filtros.status) params.append("status", filtros.status);
  if (filtros.mes) params.append("mes", filtros.mes);
  if (filtros.busca) params.append("busca", filtros.busca);
  if (filtros.projetoId) params.append("projetoId", filtros.projetoId);
  if (filtros.page) params.append("page", String(filtros.page));
  if (filtros.perPage) params.append("perPage", String(filtros.perPage));
  const r = await api.get(`/financeiro/parcelas?${params.toString()}`);
  return r.data.data as ListagemParcelas;
}

export async function atualizarContrato(id: string, data: {
  observacoes?: string | null;
  valorTotal?: number;
  valorSetup?: number;
  contratoAssinadoEm?: string | null;
  valorMensal?: number;
  diaVencimento?: number | null;
  inicioCobranca?: string | null;
  fimCobranca?: string | null;
}) {
  const r = await api.patch(`/financeiro/contratos/${id}`, data);
  return r.data.data as Contrato;
}

export type MensalidadeStatus = "em_dia" | "vence_hoje" | "atrasada" | "sem_parcela" | "aguardando_definicao";

export interface MensalidadeItem {
  id: string;
  projetoId: string;
  projeto: { id: string; nome: string; cliente: string };
  valorMensal: number;
  diaVencimento: number | null;
  inicioCobranca: string | null;
  fimCobranca: string | null;
  ativa: boolean;
  proximaParcela: string | null;
  proximaParcelaId: string | null;
  ultimaPaga: string | null;
  status: MensalidadeStatus;
  diasAtraso: number;
  totalPendentes: number;
}

export async function listarMensalidades() {
  const r = await api.get("/financeiro/mensalidades");
  return r.data.data as MensalidadeItem[];
}

export async function cancelarMensalidade(contratoId: string, motivo?: string, dataFim?: string) {
  const r = await api.post(`/financeiro/mensalidades/${contratoId}/cancelar`, { motivo, dataFim });
  return r.data.data;
}

export async function reativarMensalidade(contratoId: string) {
  const r = await api.post(`/financeiro/mensalidades/${contratoId}/reativar`);
  return r.data.data;
}

export interface MetricasFinanceiras {
  totalRecebido: number;
  totalPendente: number;
  totalAtrasado: number;
  aReceber30d: number;
  mrr: number;
  mensalidadesAtivas: number;
}

export async function buscarMetricas() {
  const r = await api.get("/financeiro/metricas");
  return r.data.data as MetricasFinanceiras;
}

export interface CronogramaMes {
  mes: string; // YYYY-MM
  total: number;
  itens: Array<{
    id: string;
    valor: number;
    valorPago: number;
    vencimento: string;
    status: string;
    projetoId: string;
    projeto: { id: string; nome: string; cliente: string };
    modalidade: string;
    numero: number;
  }>;
}

export async function buscarCronograma(meses: number = 12) {
  const r = await api.get(`/financeiro/cronograma?meses=${meses}`);
  return r.data.data as CronogramaMes[];
}
